// hooks/useSessionTimeout.ts - FIXED (No Blinking)
import { useEffect, useRef, useCallback, useState } from 'react';

interface UseSessionTimeoutProps {
  onTimeout: () => void;
  onWarning?: () => void;
  isAuthenticated: boolean;
}

const IDLE_TIMEOUT = (Number.parseInt(process.env.REACT_APP_IDLE_TIMEOUT_MINUTES || '30') * 60 * 1000);
const WARNING_TIME = (Number.parseInt(process.env.REACT_APP_SESSION_WARNING_MINUTES || '2') * 60 * 1000);

export const useSessionTimeout = ({
  onTimeout,
  onWarning,
  isAuthenticated
}: UseSessionTimeoutProps) => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const handleTimeout = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    onTimeout();
  }, [onTimeout, clearAllTimers]);

  const startCountdown = useCallback(() => {
    setTimeLeft(WARNING_TIME);

    if (countdownRef.current) clearInterval(countdownRef.current);

    countdownRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        const newTime = prev - 1000;
        if (newTime <= 0) {
          if (countdownRef.current) clearInterval(countdownRef.current);
          return 0;
        }
        return newTime;
      });
    }, 1000);
  }, []);

  const resetTimer = useCallback(() => {
    if (!isAuthenticated) return;

    const now = Date.now();
    lastActivityRef.current = now;

    // Don't reset if warning is already showing
    if (showWarning) {
      return;
    }

    clearAllTimers();

    // Set warning timer
    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true);
      startCountdown();
      if (onWarning) onWarning();
    }, IDLE_TIMEOUT - WARNING_TIME);

    // Set logout timer
    timeoutRef.current = setTimeout(() => {
      handleTimeout();
    }, IDLE_TIMEOUT);
  }, [isAuthenticated, handleTimeout, clearAllTimers, startCountdown, onWarning, showWarning]);

  const extendSession = useCallback(() => {
    setShowWarning(false);
    // Wait a bit before resetting timer to prevent immediate re-trigger
    setTimeout(() => {
      resetTimer();
    }, 100);
  }, [resetTimer]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearAllTimers();
      setShowWarning(false);
      return;
    }

    // Activity events to track
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      //  CRITICAL: Ignore activity if warning is showing
      if (showWarning) {
        return;
      }

      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityRef.current;

      // Debounce by 1 second
      if (timeSinceLastActivity > 1000) {
        resetTimer();
      }
    };

    // Initial timer setup
    resetTimer();

    // Add event listeners
    events.forEach((event) => {
      globalThis.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      clearAllTimers();
      events.forEach((event) => {
        globalThis.removeEventListener(event, handleActivity);
      });
    };
  }, [resetTimer, showWarning, isAuthenticated, clearAllTimers]);

  return {
    showWarning,
    timeLeft,
    extendSession,
    handleTimeout,
  };
};